import { Component, OnInit, TemplateRef, inject, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { PlannedPurpose } from 'src/app/core/models/purpose';
import { isQualitativeKpi, isQuantitativeKpi } from '../../utils/shared';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-planning-table-kpi-dialog',
    styles: [':host { display: block; max-width: 500px }'],
    template: `
    <h4 class="text-center">{{ 'PLANNING.KPI.EDIT' | translate }}</h4>
    <form [formGroup]="kpiForm" class="row">
      <mat-form-field appearance="outline" class="col-12">
        <mat-label>{{ 'PLANNING.KPI.NAME' | translate }}</mat-label>
        <input matInput formControlName="title" required />
        @if (title?.invalid) {
          <mat-error>{{ 'PLANNING.KPI.NAME_ERROR' | translate }}</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="col-12">
        <mat-label>{{ 'PLANNING.KPI.NOTES' | translate }}</mat-label>
        <textarea rows="6" matInput formControlName="description" required></textarea>
        @if (description?.invalid) {
          <mat-error>{{ 'PLANNING.KPI.NOTES_ERROR' | translate }}</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="col-12">
        <mat-label>{{ 'PLANNING.KPI.PROGRESS' | translate }}</mat-label>
        <input #progressRef (click)="progressRef.select()" matInput mask="0999" formControlName="progress" required />
        @if (progress?.invalid) {
          <mat-error class="mb-2">
            @if (progress?.hasError('message')) {
              {{progress?.errors?.['message']}}
            } @else {
              {{ 'PLANNING.KPI.PROGRESS_ERROR' | translate }}
            }
            <ng-template #required>{{ 'PLANNING.KPI.PROGRESS_ERROR' | translate }}</ng-template>
          </mat-error>
        }
      </mat-form-field>

      @if (isQuantitativeKpi(kpi)) {
        <mat-form-field appearance="outline" class="col-12">
          <mat-label>{{ 'PLANNING.KPI.TARGET' | translate }}</mat-label>
          <input #targetRef (click)="targetRef.select()" matInput mask="0999" formControlName="target" required />
          @if (target?.invalid) {
            <mat-error>{{ 'PLANNING.KPI.TARGET_ERROR' | translate }}</mat-error>
          }
        </mat-form-field>
      }
    </form>

    <section class="d-flex justify-content-center my-3">
      <button
        mat-icon-button
        [disableRipple]="true"
        [matTooltip]="'PLANNING.KPI.SHOW_INFO' | translate"
        color="accent"
        (click)="displaySuggestedText()"
        >
        <mat-icon>info</mat-icon>
      </button>
      <button mat-flat-button mat-dialog-close>{{ 'CANCEL' | translate }}</button>
      <button
        [disabled]="!kpiForm.valid || !kpiForm.dirty"
        mat-flat-button
        color="accent"
        [mat-dialog-close]="formValue"
        >
        {{ 'CONFIRM' | translate }}
      </button>
    </section>
    <ng-template #suggestedText let-description>
      <h2 class="text-center">Istruzioni</h2>
      <p class="text-center" [innerHtml]="'PLANNING.KPI.ADD_EDIT_INFO' | translate"></p>
      <div class="d-flex justify-content-center mt-4">
        <button (click)="closeSuggestedText()" mat-flat-button color="accent">{{ 'CLOSE' | translate }}</button>
      </div>
    </ng-template>
    `,
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatIconButton, MatTooltip, MatIcon, MatButton, MatDialogClose, TranslateModule]
})
export class PlanningTableKpiDialogComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private dialog = inject(MatDialog);
  kpi = inject<PlannedPurpose.QualitativeKpi | PlannedPurpose.QuantitativeKpi>(MAT_DIALOG_DATA);

  kpiForm!: UntypedFormGroup;

  suggestedTextDialogRef!: MatDialogRef<any>;

  readonly suggestedTextTpl = viewChild.required<TemplateRef<any>>('suggestedText');

  isQuantitativeKpi = isQuantitativeKpi;
  isQualitativeKpi = isQualitativeKpi;

  ngOnInit(): void {
    this.kpiForm = this.fb.group({
      title: [this.kpi.title ?? '', Validators.required],
      description: [this.kpi.description ?? '', Validators.required],
      progress: [this.kpi.progress ?? 0, Validators.required],
    });

    if (this.kpi.type === PlannedPurpose.KpiType.QUANTITATIVE) {
      this.kpiForm.addControl(
        'target',
        this.fb.control(this.kpi.target ?? 1, [Validators.required, Validators.min(1)])
      );
      this.kpiForm.get('target')?.updateValueAndValidity();
    }
  }

  get formValue(): PlannedPurpose.QualitativeKpi | PlannedPurpose.QuantitativeKpi {
    const values = this.kpiForm.getRawValue();

    if (this.kpi.type === 1) {
      return new PlannedPurpose.QuantitativeKpi(values.title, values.description, values.progress, values.target);
    } else {
      return new PlannedPurpose.QualitativeKpi(values.title, values.description, values.progress);
    }
  }

  get kpiType() {
    return this.kpi.type;
  }

  get title() {
    return this.kpiForm.get('title');
  }

  get description() {
    return this.kpiForm.get('description');
  }

  get progress() {
    return this.kpiForm.get('progress');
  }

  get target() {
    return this.kpiForm.get('target');
  }

  displaySuggestedText() {
    this.suggestedTextDialogRef = this.dialog.open(this.suggestedTextTpl(), {
      panelClass: 'col-md-5',
    });
  }

  closeSuggestedText() {
    this.suggestedTextDialogRef?.close();
  }
}
